import { useEffect, useMemo } from "react";
import styles from "./edit-task-modal.module.css";
import { DynamicModal, ModalComponent, ModalProps } from "frontend/modal/dynamic-modal";
import CloseIcon from "frontend/ui-components/design-system/icons/close-icon";
import { replaceColorOpacity } from "frontend/utils/color-utils";
import { TaskElement } from "../../schema";
import { EditTaskModalDropdown } from "./edit-task-modal-dropdown";
import { TagsIcon, DueDateIcon, AssigneeIcon } from "./edit-task-modal-icons";
import { TaskColorPicker } from "./dropdowns/task-color-picker";
import { ITaskController } from "../../controller";
import useObservableController from "elements/hooks/use-observable-controller";
import { TaskTagEditor } from "./dropdowns/task-tag-editor";
import { TaskDatePicker } from "./dropdowns/task-date-picker";
import { TaskAssigneePicker } from "./dropdowns/task-assignee-picker";
import { EditableTextArea } from "./editable-text-area";
import textAreaStyles from "./editable-text-area.module.css";
import { TagDisplay } from "./tag-display";
import { useCanvasElementById } from "frontend/subscriptions";
import useCanvasElement from "elements/hooks/use-canvas-element";
import consts from "shared/consts";
import { BoardContext } from "elements/index";
import { useAllTags, processTagEntries } from "../hooks/use-task-tags";
import { useSetAtom } from "jotai";
import { submenuDataAtom } from "state-atoms";
import useDebounceCallback from "frontend/hooks/use-debounce-callback";

type ModalContentProps = {
  controller: ITaskController;
} & ModalProps<void>;

const ModalContent = ({ controller, onClose }: ModalContentProps) => {
  useObservableController(controller);
  const { element: taskElement, id: taskId, context } = controller;
  const { isReadOnly } = context;
  const setSubmenuData = useSetAtom(submenuDataAtom);

  const reportEventChanged = useDebounceCallback((field: string) => {
    controller.reportFieldChanged(field, "task-modal");
  }, 200);

  const sidebarBackground = useMemo(() => {
    // If white, do gray because we like colors
    if (taskElement.fill.slice(1, 7).toLowerCase() === "ffffff") {
      return "#F8F9FC";
    }
    return replaceColorOpacity(taskElement.fill, 0.15 * 255);
  }, [taskElement.fill]);

  const allTagEntries = useAllTags(controller);
  const tagEntries = processTagEntries(allTagEntries);

  useEffect(() => {
    if (isReadOnly) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadOnly]);

  return (
    <div className={styles.background} style={{ backgroundColor: taskElement.fill }}>
      <div className={styles.container}>
        <div className={styles.tagsContainer}>
          <TagDisplay controller={controller} />
        </div>
        <EditableTextArea
          controller={controller}
          field="title"
          placeholder="Add title"
          className={textAreaStyles.title}
          autoResize={true}
          onFieldChanged={(field) => reportEventChanged(field)}
        />
        <EditableTextArea
          controller={controller}
          field="description"
          placeholder="Add description..."
          className={textAreaStyles.description}
          autoResize={true}
          onFieldChanged={(field) => reportEventChanged(field)}
        />
        <div className={styles.sidebar} style={{ backgroundColor: sidebarBackground }}>
          <div className={styles.closeIcon} onClick={onClose}>
            <CloseIcon color="#848199" strokeWidth={1.2} />
          </div>
          <div className={styles.sidebarContent}>
            {!controller.isPartOfGantt() && (
              <EditTaskModalDropdown
                icon={
                  <div
                    className={styles.colorIndicator}
                    style={{
                      backgroundColor: taskElement.fill,
                      border: taskElement.fill.slice(1, 7).toLowerCase() === "ffffff" ? "1px solid #DEE1EB" : undefined,
                    }}
                  />
                }
                title="Task Color"
              >
                {(onDismiss) => (
                  <TaskColorPicker
                    onChange={(color) => {
                      controller.patchElement(taskId, (task: TaskElement) => {
                        task.fill = color;
                      });
                      // Update submenu data so next task created uses this color
                      setSubmenuData((previous) => ({
                        ...previous,
                        task: {
                          ...previous.task,
                          color,
                        },
                      }));
                      reportEventChanged("color");
                      onDismiss();
                    }}
                  />
                )}
              </EditTaskModalDropdown>
            )}
            <EditTaskModalDropdown icon={<TagsIcon />} title="Tags">
              {(_onDismiss) => (
                <TaskTagEditor
                  controller={controller}
                  tagEntries={tagEntries}
                  onTagChanged={() => reportEventChanged("tags")}
                />
              )}
            </EditTaskModalDropdown>
            <EditTaskModalDropdown
              icon={<AssigneeIcon assignee={controller.getAssignee()} />}
              title={controller.getAssignee()?.name || "Assignee"}
            >
              {(onDismiss) => (
                <TaskAssigneePicker
                  assignee={controller.getAssignee()}
                  onSelected={(assignee) => {
                    controller.setAssignee(assignee);
                    reportEventChanged("assignee");
                    onDismiss();
                  }}
                />
              )}
            </EditTaskModalDropdown>
            <EditTaskModalDropdown icon={<DueDateIcon />} title={controller.getDateTitle()}>
              {(_onDismiss) => (
                <TaskDatePicker controller={controller} onDateChanged={() => reportEventChanged("date")} />
              )}
            </EditTaskModalDropdown>
          </div>
        </div>
      </div>
    </div>
  );
};

type EditTaskModalWithHooksProps = {
  taskId: string;
  context: BoardContext;
} & ModalProps<void>;

const EditTaskModalWithHooks = ({ taskId, context, onClose, onComplete }: EditTaskModalWithHooksProps) => {
  const { reflect } = context;

  const elementData = useCanvasElementById(reflect, taskId, null);
  const { controller: _controller, element } = useCanvasElement(
    taskId,
    consts.CANVAS_ELEMENTS.TASK,
    context,
    elementData as TaskElement
  );

  const controller = _controller as ITaskController | null;

  if (!controller || !element) {
    return null;
  }

  return <ModalContent controller={controller} onClose={onClose} onComplete={onComplete} />;
};

type EditTaskModalProps = {
  controller?: ITaskController;
  taskId?: string;
  context?: BoardContext;
} & ModalProps<void>;

const EditTaskModal: ModalComponent<void, EditTaskModalProps> = ({
  controller,
  taskId,
  context,
  onClose,
  onComplete,
}: EditTaskModalProps) => {
  if (controller) {
    return <ModalContent controller={controller} onClose={onClose} onComplete={onComplete} />;
  }

  if (!taskId || !context) {
    console.error("EditTaskModal: Must provide either controller OR (taskId + context)");
    return null;
  }

  return <EditTaskModalWithHooks taskId={taskId} context={context} onClose={onClose} onComplete={onComplete} />;
};

EditTaskModal.componentName = "EditTaskModal";
DynamicModal.register(EditTaskModal, {
  dimBackground: true,
  dismissOnEscape: true,
  dismissOnBackgroundClick: true,
});

export default EditTaskModal;
